/**
 * Trainer UI Validation Utilities
 *
 * Extracted validation logic for testability.
 */

export interface RubricFormData {
  name: string;
  description: string;
  passingScore: number;
  dimensions: DimensionFormData[];
}

export interface DimensionFormData {
  id: string;
  name: string;
  description: string;
  weight: number;
  rules: RuleFormData[];
}

export interface RuleFormData {
  id: string;
  type: 'keyword' | 'regex';
  pattern: string;
  score: number;
  required: boolean;
  caseSensitive: boolean;
}

/**
 * Generates a unique ID for form elements.
 * Uses crypto.randomUUID() for uniqueness.
 */
export function generateFormId(): string {
  return crypto.randomUUID();
}

/**
 * Validates rubric form data.
 * Returns array of error messages (empty if valid).
 */
export function validateRubricForm(data: RubricFormData): string[] {
  const errors: string[] = [];

  // Basic info validation
  if (!data.name.trim()) {
    errors.push('Rubric name is required');
  }

  if (data.passingScore < 0 || data.passingScore > 100) {
    errors.push('Passing score must be between 0 and 100');
  }

  // Dimensions validation
  if (data.dimensions.length === 0) {
    errors.push('At least one dimension is required');
  }

  data.dimensions.forEach((dim, i) => {
    if (!dim.name.trim()) {
      errors.push(`Dimension ${i + 1}: name is required`);
    }
    if (dim.weight <= 0) {
      errors.push(`Dimension ${i + 1}: weight must be positive`);
    }
    if (dim.rules.length === 0) {
      errors.push(`Dimension ${i + 1}: at least one rule is required`);
    }

    // Rules validation
    dim.rules.forEach((rule, j) => {
      if (!rule.pattern.trim()) {
        errors.push(`Dimension ${i + 1}, Rule ${j + 1}: pattern is required`);
      }
    });
  });

  return errors;
}

/**
 * Validates that a rubric can be used for assessment creation.
 * Only published rubrics are allowed.
 */
export function canUseRubricForAssessment(rubricStatus: string): boolean {
  return rubricStatus === 'published';
}
